import { AccountBalanceWallet, AttachMoney, Business, Description, Home, Percent, VpnKey } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	Switch,
	TextField,
	Typography,
	Tooltip,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { globalRemoveMaskReal2 } from "../../../shared/utils/globalMask-document";

interface PlanListEditorProps {
	value: string | any[];
	onChange: (newValue: string) => void;
	planRevisionStatus?: string | null;
	revisionPlanId?: string | null;
	editMode?: "full" | "statusOnly";
	allowRemove?: boolean;
}

export const PlanListEditor: React.FC<PlanListEditorProps> = ({ value, onChange, planRevisionStatus, revisionPlanId, editMode = "full", allowRemove = false }) => {
	const [plans, setPlans] = useState<any[]>([]);
	const lastAutoApplyKeyRef = useRef<string | null>(null);
	const isStatusOnly = editMode === "statusOnly";
	const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
	const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

	const isRevisionActive = (val: unknown): boolean => {
		if (val === null || val === undefined) return false;
		if (typeof val === "boolean") return val;
		if (typeof val === "number") return val > 0;
		if (typeof val === "string") {
			const normalized = val.trim().toLowerCase();
			return ["active", "ativo", "true", "1", "yes", "sim"].includes(normalized);
		}
		if (typeof val === "object") {
			const anyVal = val as any;
			return isRevisionActive(anyVal?.status ?? anyVal?.value ?? anyVal?.active);
		}
		return Boolean(val);
	};

	const getPlanIdFromEntry = (entry: any): string | null => {
		const candidate =
			entry?.planId ??
			entry?.plan_id ??
			entry?.plan?.id ??
			entry?.plan?.planId ??
			entry?.plan?.plan_id ??
			entry?.id;
		if (candidate === null || candidate === undefined) return null;
		return String(candidate);
	};

	const toStatus = (val: unknown): string => String(val ?? "").trim().toLowerCase();

	const currencyMask = (value: string) => {
		const clean = value.replace(/\D/g, "");
		if (!clean) return "0,00";

		const padded = clean.padStart(3, "0");
		const integerPart = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
		const decimalPart = padded.slice(-2);

		const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
		return `${formattedInteger},${decimalPart}`;
	};

	useEffect(() => {
		try {
			const parsed = typeof value === "string" ? JSON.parse(value) : value;
			if (Array.isArray(parsed)) {
				// Só aplicamos o "modo revisão" quando a flag vier realmente ativa.
				// Se vier inactive/false/0, devemos manter os statuses originais.
				const revisionModeEnabled = isRevisionActive(planRevisionStatus);
				const planIds = parsed.map(getPlanIdFromEntry).filter(Boolean) as string[];
				const revisionNamedPlanId = planIds.find((id) => {
					const normalized = String(id).trim().toLowerCase();
					return normalized === "revision" || normalized === "revison" || normalized === "revisao";
				});
				const revisionIdFromStatusObjectRaw = getPlanIdFromEntry(planRevisionStatus);
				const revisionIdFromPropRaw = revisionPlanId ? String(revisionPlanId) : null;
				const revisionIdFromStatusObject =
					revisionIdFromStatusObjectRaw && planIds.includes(revisionIdFromStatusObjectRaw)
						? revisionIdFromStatusObjectRaw
						: null;
				const revisionIdFromProp = revisionIdFromPropRaw && planIds.includes(revisionIdFromPropRaw) ? revisionIdFromPropRaw : null;
				// Em modo revisão, só nos interessa o plano de revisão (ex.: planId "revision").
				// Não inferimos pelo plano ativo nem pelo primeiro item, para não ativar um plano errado.
				const effectiveRevisionPlanId = revisionModeEnabled ? revisionNamedPlanId ?? revisionIdFromProp ?? revisionIdFromStatusObject ?? null : null;

				const shouldEnsureRevisionActive = revisionModeEnabled && Boolean(effectiveRevisionPlanId);
				const revisionNeedsActivation = shouldEnsureRevisionActive
					? (() => {
						const target = parsed.find((p: any) => getPlanIdFromEntry(p) === String(effectiveRevisionPlanId));
						const status = toStatus(target?.status ?? target?.plan?.status);
						return status !== "active" && status !== "ativo";
					})()
					: false;

				if (process.env.NODE_ENV !== "production") {
					console.groupCollapsed("[PlanListEditor] revision debug");
					console.log("planRevisionStatus (raw):", planRevisionStatus);
					console.log("revisionModeEnabled:", revisionModeEnabled);
					console.log("revisionPlanId (prop):", revisionPlanId);
					console.log("planIds:", planIds);
					console.log("revisionIdFromPropRaw:", revisionIdFromPropRaw);
					console.log("revisionIdFromProp (validated):", revisionIdFromProp);
					console.log("revisionIdFromStatusObjectRaw:", revisionIdFromStatusObjectRaw);
					console.log("revisionIdFromStatusObject (validated):", revisionIdFromStatusObject);
					console.log("effectiveRevisionPlanId:", effectiveRevisionPlanId);
					console.log("shouldEnsureRevisionActive:", shouldEnsureRevisionActive);
					console.log("revisionNeedsActivation:", revisionNeedsActivation);
					console.table(
						parsed.map((p: any, idx: number) => ({
							idx,
							planId: getPlanIdFromEntry(p),
							status: p?.status ?? p?.plan?.status,
							description: p?.plan?.description ?? p?.plan?.name ?? "",
						}))
					);
					console.groupEnd();
				}

				if (!shouldEnsureRevisionActive) {
					lastAutoApplyKeyRef.current = null;
				}

				const normalizedPlans = revisionNeedsActivation
					? parsed.map((plan: any) =>
						getPlanIdFromEntry(plan) === String(effectiveRevisionPlanId)
							? {
								...plan,
								status: "active",
							}
							: plan
					)
					: parsed;

				if (process.env.NODE_ENV !== "production" && revisionNeedsActivation) {
					console.groupCollapsed("[PlanListEditor] revision normalized (activation only)");
					console.table(
						normalizedPlans.map((p: any, idx: number) => ({
							idx,
							planId: getPlanIdFromEntry(p),
							status: p?.status,
							description: p?.plan?.description ?? p?.plan?.name ?? "",
						}))
					);
					console.groupEnd();
				}

				// Apply mask to initial values
				const maskedPlans = normalizedPlans.map((plan: any) => ({
					...plan,
					lmi: currencyMask((Number(plan.lmi) || 0).toFixed(2)),
					residentialFee: currencyMask((Number(plan.residentialFee) || 0).toFixed(2)),
					commercialFee: currencyMask((Number(plan.commercialFee) || 0).toFixed(2)),
					rent_amount: currencyMask((Number(plan.rent_amount) || 0).toFixed(2)),
					setup_transfer: currencyMask((Number(plan.setup_transfer) || 0).toFixed(2)),
					bonus_amount: currencyMask((Number(plan.bonus_amount) || 0).toFixed(2)),
					comission_value: currencyMask((Number(plan.comission_value) || 0).toFixed(2)),
				}));
				setPlans(maskedPlans);

				if (revisionNeedsActivation) {
					const applyKey = `${String(planRevisionStatus)}|${String(effectiveRevisionPlanId)}|${maskedPlans.length}|activateRevision`;
					if (lastAutoApplyKeyRef.current === applyKey) return;
					lastAutoApplyKeyRef.current = applyKey;

					const unmaskedPlans = maskedPlans.map((plan) => {
						const lmi = globalRemoveMaskReal2(plan.lmi) || "0";
						const residentialFee = globalRemoveMaskReal2(plan.residentialFee) || "0";
						const commercialFee = globalRemoveMaskReal2(plan.commercialFee) || "0";
						const rent_amount = globalRemoveMaskReal2(plan.rent_amount) || "0";
						const setup_transfer = globalRemoveMaskReal2(plan.setup_transfer) || "0";
						const bonus_amount = globalRemoveMaskReal2(plan.bonus_amount) || "0";
						const comission_value = globalRemoveMaskReal2(plan.comission_value) || "0";

						const rentVal = parseFloat(rent_amount);
						const resFeeVal = parseFloat(residentialFee);
						const comFeeVal = parseFloat(commercialFee);

						const residentialFee_assurance_month_amount =
							rentVal > 0 && resFeeVal > 0 ? Number((rentVal * (resFeeVal / 100)).toFixed(2)) : 0;

						const commercialFee_assurance_month_amount =
							rentVal > 0 && comFeeVal > 0 ? Number((rentVal * (comFeeVal / 100)).toFixed(2)) : 0;

						return {
							...plan,
							lmi,
							residentialFee,
							commercialFee,
							rent_amount,
							setup_transfer,
							bonus_amount,
							comission_value,
							residentialFee_assurance_month_amount,
							commercialFee_assurance_month_amount,
						};
					});

					onChange(JSON.stringify(unmaskedPlans, null, 2));
				}
			} else {
				setPlans([]);
				lastAutoApplyKeyRef.current = null;
			}
		} catch (e) {
			console.error("Error parsing planlist JSON", e);
			setPlans([]);
			lastAutoApplyKeyRef.current = null;
		}
	}, [value, planRevisionStatus, revisionPlanId]);

	const updatePlan = (index: number, field: string, val: string, options?: { mask?: boolean }) => {
		const newPlans = [...plans];
		const shouldMask = options?.mask ?? true;
		const nextVal = shouldMask ? currencyMask(val) : val;
		newPlans[index] = { ...newPlans[index], [field]: nextVal };
		setPlans(newPlans);

		// Unmask values for the JSON output
		const unmaskedPlans = newPlans.map((plan) => {
			const lmi = globalRemoveMaskReal2(plan.lmi) || "0";
			const residentialFee = globalRemoveMaskReal2(plan.residentialFee) || "0";
			const commercialFee = globalRemoveMaskReal2(plan.commercialFee) || "0";
			const rent_amount = globalRemoveMaskReal2(plan.rent_amount) || "0";
			const setup_transfer = globalRemoveMaskReal2(plan.setup_transfer) || "0";
			const bonus_amount = globalRemoveMaskReal2(plan.bonus_amount) || "0";
			const comission_value = globalRemoveMaskReal2(plan.comission_value) || "0";

			// Calculate assurance amounts
			const rentVal = parseFloat(rent_amount);
			const resFeeVal = parseFloat(residentialFee);
			const comFeeVal = parseFloat(commercialFee);

			const residentialFee_assurance_month_amount = rentVal > 0 && resFeeVal > 0 ? Number((rentVal * (resFeeVal / 100)).toFixed(2)) : 0;

			const commercialFee_assurance_month_amount = rentVal > 0 && comFeeVal > 0 ? Number((rentVal * (comFeeVal / 100)).toFixed(2)) : 0;

			return {
				...plan,
				lmi,
				residentialFee,
				commercialFee,
				rent_amount,
				setup_transfer,
				bonus_amount,
				comission_value,
				residentialFee_assurance_month_amount,
				commercialFee_assurance_month_amount,
			};
		});

		onChange(JSON.stringify(unmaskedPlans, null, 2));
	};

	const removePlan = (index: number) => {
		const nextPlans = plans.filter((_, i) => i !== index);
		setPlans(nextPlans);

		const unmaskedPlans = nextPlans.map((plan) => {
			const lmi = globalRemoveMaskReal2(plan.lmi) || "0";
			const residentialFee = globalRemoveMaskReal2(plan.residentialFee) || "0";
			const commercialFee = globalRemoveMaskReal2(plan.commercialFee) || "0";
			const rent_amount = globalRemoveMaskReal2(plan.rent_amount) || "0";
			const setup_transfer = globalRemoveMaskReal2(plan.setup_transfer) || "0";
			const bonus_amount = globalRemoveMaskReal2(plan.bonus_amount) || "0";
			const comission_value = globalRemoveMaskReal2(plan.comission_value) || "0";

			const rentVal = parseFloat(rent_amount);
			const resFeeVal = parseFloat(residentialFee);
			const comFeeVal = parseFloat(commercialFee);

			const residentialFee_assurance_month_amount =
				rentVal > 0 && resFeeVal > 0 ? Number((rentVal * (resFeeVal / 100)).toFixed(2)) : 0;

			const commercialFee_assurance_month_amount =
				rentVal > 0 && comFeeVal > 0 ? Number((rentVal * (comFeeVal / 100)).toFixed(2)) : 0;

			return {
				...plan,
				lmi,
				residentialFee,
				commercialFee,
				rent_amount,
				setup_transfer,
				bonus_amount,
				comission_value,
				residentialFee_assurance_month_amount,
				commercialFee_assurance_month_amount,
			};
		});

		onChange(JSON.stringify(unmaskedPlans, null, 2));
	};

	const openRemoveDialog = (index: number) => {
		setPendingRemoveIndex(index);
		setRemoveDialogOpen(true);
	};

	const closeRemoveDialog = () => {
		setRemoveDialogOpen(false);
		setPendingRemoveIndex(null);
	};

	const confirmRemoveDialog = () => {
		if (pendingRemoveIndex === null) return;
		removePlan(pendingRemoveIndex);
		closeRemoveDialog();
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Dialog open={removeDialogOpen} onClose={closeRemoveDialog} maxWidth="xs" fullWidth>
				<DialogTitle>Remover plano</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mt: 1 }}>
						Tem certeza que deseja remover este plano da lista?
					</Typography>
					{pendingRemoveIndex !== null && plans[pendingRemoveIndex] && (
						<Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
							{plans[pendingRemoveIndex]?.plan?.description || plans[pendingRemoveIndex]?.description || "Plano"}
						</Typography>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={closeRemoveDialog} variant="outlined" color="primary">
						Cancelar
					</Button>
					<Button onClick={confirmRemoveDialog} variant="contained" color="error">
						Remover
					</Button>
				</DialogActions>
			</Dialog>

			{plans.map((plan, index) => (
				<Card
					key={index}
					elevation={0}
					sx={{
						mb: 3,
						borderRadius: 3,
						border: "1px solid #e0e0e0",
						backgroundColor: "#fff",
						boxShadow: "rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px",
						transition: "box-shadow 0.2s ease, transform 0.2s ease",
						"&:hover": {
							boxShadow: "rgba(17, 17, 26, 0.08) 0px 4px 16px, rgba(17, 17, 26, 0.12) 0px 0px 10px",
							transform: "translateY(-1px)",
						},
					}}
				>
					<CardContent>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: "#7924c7", mb: 0.5 }}>
									{plan.plan.description || "Plano Sem Descrição"}
								</Typography>
								<Typography
									variant="caption"
									sx={{
										color: "#666",
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										bgcolor: "#f5f5f5",
										px: 1,
										py: 0.5,
										borderRadius: 1,
										width: "fit-content",
									}}
								>
									<VpnKey fontSize="inherit" sx={{ color: "#999" }} />
									<span style={{ fontWeight: 600 }}>ID:</span> {plan.planId}
									<span style={{ margin: "0 4px", color: "#ddd" }}>|</span>
									<span style={{ fontWeight: 600 }}>Produto:</span> {plan.productId}
								</Typography>
							</Box>
							<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 0.5 }}>
								{allowRemove && (
									<Tooltip title="Remover plano">
										<IconButton
											size="small"
											onClick={() => openRemoveDialog(index)}
											aria-label="Remover plano"
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								)}
								<Switch
									checked={(plan.status || "active") === "active"}
									onChange={(e) => updatePlan(index, "status", e.target.checked ? "active" : "inactive", { mask: false })}
									inputProps={{ "aria-label": "Alterar status do plano" }}
									sx={{ mr: -0.5 }}
								/>
								<Chip
									label={plan.status === "active" ? "ATIVO" : plan.status?.toUpperCase() || "ATIVO"}
									sx={{
										bgcolor: plan.status === "active" ? "#e8f5e9" : "#f5f5f5",
										color: plan.status === "active" ? "#2e7d32" : "#666",
										fontWeight: "bold",
										borderRadius: 1,
										height: 24,
									}}
									size="small"
								/>
							</Box>
						</Box>

						<Divider sx={{ mb: 2 }} />

						<Grid container spacing={2}>
							{/* Structural Fields (Disabled) */}
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Descrição"
									value={plan.plan.description || ""}
									disabled
									variant="filled"
									size="small"
									InputProps={{
										disableUnderline: true,
										startAdornment: (
											<InputAdornment position="start">
												<Description fontSize="small" />
											</InputAdornment>
										),
										style: { borderRadius: 8, backgroundColor: "#f9f9f9" },
									}}
									sx={{ "& .MuiInputLabel-root": { color: "#888" } }}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Client ID"
									value={plan.clientId || ""}
									disabled
									variant="filled"
									size="small"
									InputProps={{
										disableUnderline: true,
										style: { borderRadius: 8, backgroundColor: "#f9f9f9" },
									}}
									sx={{ "& .MuiInputLabel-root": { color: "#888" } }}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Meses Aluguel"
									value={plan.rent_months_count || ""}
									disabled
									variant="filled"
									size="small"
									InputProps={{
										disableUnderline: true,
										style: { borderRadius: 8, backgroundColor: "#f9f9f9" },
									}}
									sx={{ "& .MuiInputLabel-root": { color: "#888" } }}
								/>
							</Grid>

							{/* Editable Fields */}
							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="LMI (x)"
									value={plan.lmi || ""}
									onChange={(e) => updatePlan(index, "lmi", e.target.value)}
										disabled={isStatusOnly}
									size="small"
								/>
							</Grid>
							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="Taxa Residencial"
									value={plan.residentialFee || ""}
									onChange={(e) => updatePlan(index, "residentialFee", e.target.value)}
										disabled={isStatusOnly}
									size="small"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Percent fontSize="small" />
											</InputAdornment>
										),
										startAdornment: (
											<InputAdornment position="start">
												<Home fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="Taxa Comercial"
									value={plan.commercialFee || ""}
									onChange={(e) => updatePlan(index, "commercialFee", e.target.value)}
										disabled={isStatusOnly}
									size="small"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Percent fontSize="small" />
											</InputAdornment>
										),
										startAdornment: (
											<InputAdornment position="start">
												<Business fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="Valor Aluguel"
									value={plan.rent_amount || ""}
									onChange={(e) => updatePlan(index, "rent_amount", e.target.value)}
										disabled={isStatusOnly}
									size="small"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<AttachMoney fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="Setup Transfer"
									value={plan.setup_transfer || ""}
									onChange={(e) => updatePlan(index, "setup_transfer", e.target.value)}
										disabled={isStatusOnly}
									size="small"
								/>
							</Grid>
							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="Valor Bônus"
									value={plan.bonus_amount || ""}
									onChange={(e) => updatePlan(index, "bonus_amount", e.target.value)}
										disabled={isStatusOnly}
									size="small"
								/>
							</Grid>
							<Grid item xs={6} md={3}>
								<TextField
									fullWidth
									label="Comissão (%)"
									value={plan.comission_value || ""}
									onChange={(e) => updatePlan(index, "comission_value", e.target.value)}
										disabled={isStatusOnly}
									size="small"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Percent fontSize="small" />
											</InputAdornment>
										),
										startAdornment: (
											<InputAdornment position="start">
												<AccountBalanceWallet fontSize="small" />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			))}
		</Box>
	);
};
